/* eslint-disable global-require */
/* eslint-disable import/no-dynamic-require */
import React, { FC, useEffect, useState } from 'react'
import { Box } from '@moonpig/launchpad-components'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { ModuleContext, ModuleImages } from '../types'
import { useTracking } from '../../contexts/tracking'
import { useProductStore } from '../../contexts/product'
import { useFavouritesState, checkFavourited } from '../../contexts/favourites'
import { isCardProduct } from '../../utils/isCardProduct'
import { GiftImageCarousel } from '../../components/GiftImageCarousel'
import { ImageCarouselVariant } from '../../components/ImageCarouselVariant'
import { useHandleFavourite } from '../../utils/favourites'
import { ProductImage, PageTypeEnum } from '../../types'
import {
  CARD_INSIDE_LEFT_INDEX,
  CARD_INSIDE_RIGHT_INDEX,
  FORMAT_POST_CARD,
  GROUP_CARD_IMAGES,
} from './constants'
import { getCardFormat } from './card-format'

type Props = {
  module: ModuleImages
  context: ModuleContext
}

const getGroupCardImages = (images: ProductImage[], cardFormat: string) => {
  return images.map((image, index) => {
    if ([CARD_INSIDE_LEFT_INDEX, CARD_INSIDE_RIGHT_INDEX].includes(index)) {
      const url = GROUP_CARD_IMAGES[cardFormat][index - 1]
      return {
        ...image,
        small: { ...image.small, jpgUrl: url },
        medium: { ...image.medium, jpgUrl: url },
        large: { ...image.large, jpgUrl: url },
      }
    }

    return image
  })
}

export const ImagesModule: FC<Props> = ({ context }: Props) => {
  const tracking = useTracking()
  const { favourites } = useFavouritesState()
  const handleFavourites = useHandleFavourite({
    removeWithConfirmation: false,
    tracking: { pageLocation: '', totalCount: 1 },
  })
  const [{ product, isGroupCardSelected }] = useProductStore()

  const cardFormat = getCardFormat(product)

  const [initialIndex, setInitialIndex] = useState(0)

  const isDynamicAgeEnabled = useExperiment('search-use-dynamic-images', {
    fallback: false,
  })

  const getProductImages = (): ProductImage[] => {
    const images = product.masterVariant.productImages.images

    if (isGroupCardSelected && cardFormat !== FORMAT_POST_CARD) {
      return getGroupCardImages(images, cardFormat)
    }

    const dynamicLargeImageUrl = product.dynamicImages?.large?.webp?.[0]?.url
    const dynamicMediumImageUrl = product.dynamicImages?.medium?.webp?.[0]?.url

    const shouldUseDynamicAgeImage =
      isDynamicAgeEnabled && dynamicLargeImageUrl && dynamicMediumImageUrl

    if (!shouldUseDynamicAgeImage) return images

    const [firstImage, ...remainingImages] = images

    return [
      {
        ...firstImage,
        small: { ...firstImage.small, webpUrl: dynamicMediumImageUrl },
        medium: { ...firstImage.medium, webpUrl: dynamicMediumImageUrl },
        large: { ...firstImage.large, webpUrl: dynamicLargeImageUrl },
      },
      ...remainingImages,
    ]
  }

  useEffect(() => {
    setInitialIndex(0)
  }, [product])

  useEffect(() => {
    setInitialIndex(isGroupCardSelected ? 1 : 0)
  }, [isGroupCardSelected])

  const isCard = isCardProduct(product.category.department)

  const moreLikeThisExperiment = useExperiment('search-use-more-like-this-v3', {
    fallback: 'control',
  })
  const moreLikeThisCarouselVariant =
    moreLikeThisExperiment === 'carousels' || moreLikeThisExperiment === 'tabs'

  const showMoreLikeThis =
    moreLikeThisCarouselVariant && context.pageType !== PageTypeEnum.PDP

  const imageCarousel = () => {
    if (isCard) {
      const productImages = getProductImages()

      return (
        <>
          <ImageCarouselVariant
            productId={product.id}
            title={product.title}
            productImages={productImages}
            initialSelectedIndex={initialIndex}
            isFavourited={checkFavourited(favourites, product)}
            onFavouriteToggle={() =>
              handleFavourites(product, checkFavourited(favourites, product), 0)
            }
            showMoreLikeThis={showMoreLikeThis}
            onImageSelected={({ index }) => {
              tracking.selectContent({
                component: 'view product image',
                label: product.id,
                action: `${index + 1}/${productImages.length}`,
              })
            }}
          />
        </>
      )
    }

    return (
      <GiftImageCarousel
        title={product.title}
        productImages={product.masterVariant.productImages.images}
        isFavourited={checkFavourited(favourites, product)}
        onFavouriteToggle={() =>
          handleFavourites(product, checkFavourited(favourites, product), 0)
        }
      />
    )
  }

  return (
    <Box mb={6} data-testid="module-images" position="relative">
      {imageCarousel()}
    </Box>
  )
}
